import React, { useMemo } from "react";

import { t } from "i18next";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Typography,
  useTheme
} from "@mui/material";

import {
  CONTENT_BOX_STYLE,
  HEADER_BOX_STYLE,
  SIZE_TEXT_STYLE,
  TITLE_STYLE,
  WARNING_BOX_STYLE,
  WARNING_ICON_STYLE,
  WARNING_TEXT_STYLE,
  getContainerStyles,
  getProgressColor
} from "./UploadProgressIndicator.styles";

interface UploadProgressIndicatorProps {
  uploaded: number;
  total: number;
  isVisible: boolean;
  isAisaProcessing?: boolean;
  isCanceling?: boolean;
  onCancel?: () => void;
  uploadedSize?: number;
  totalSize?: number;
  /** Override default status text (e.g. for feedback form) */
  statusText?: string;
  /** Override default "do not refresh" warning text */
  warningText?: string;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const getStatusText = (
  isCanceling: boolean,
  isAisaProcessing: boolean,
  total: number,
  uploaded: number
): string => {
  if (isCanceling) {
    return t("consumer.wizard.stepMedia.cancelingUploads");
  }
  if (isAisaProcessing) {
    return total === 1
      ? t("consumer.wizard.stepMedia.aisaProcessingSingle")
      : t("consumer.wizard.stepMedia.aisaProcessingMultiple");
  }
  return t("consumer.wizard.stepMedia.uploadProgress", {
    uploaded,
    total
  });
};

const UploadProgressIndicator = ({
  uploaded,
  total,
  isVisible,
  isAisaProcessing = false,
  isCanceling = false,
  onCancel,
  uploadedSize = 0,
  totalSize = 0,
  statusText: statusTextOverride,
  warningText: warningTextOverride
}: UploadProgressIndicatorProps) => {
  const theme = useTheme();

  const statusText = useMemo(
    () =>
      statusTextOverride ??
      getStatusText(isCanceling, isAisaProcessing, total, uploaded),
    [statusTextOverride, isCanceling, isAisaProcessing, total, uploaded]
  );

  const warningText = useMemo(
    () =>
      warningTextOverride ?? t("consumer.wizard.stepMedia.doNotRefreshWarning"),
    [warningTextOverride]
  );

  const cancelButtonLabel = useMemo(
    () => t("consumer.wizard.stepMedia.cancelUploads") || "Peruuta lataukset",
    []
  );

  const sizeText = useMemo(() => {
    if (totalSize === 0 || isAisaProcessing) {
      return null;
    }
    // During upload, show uploaded / total
    return `${formatBytes(uploadedSize)} / ${formatBytes(totalSize)}`;
  }, [uploadedSize, totalSize, isAisaProcessing]);

  if (!isVisible) {
    return null;
  }

  return (
    <Fade in={true} timeout={300}>
      <Box sx={getContainerStyles(theme)}>
        <Box sx={CONTENT_BOX_STYLE}>
          <Box sx={HEADER_BOX_STYLE}>
            <CircularProgress
              size={24}
              sx={getProgressColor(theme)}
              thickness={4}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0
              }}
            >
              <Typography variant="body1" sx={TITLE_STYLE}>
                {statusText}
              </Typography>
              {sizeText && (
                <Typography variant="body2" sx={SIZE_TEXT_STYLE}>
                  {sizeText}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={WARNING_BOX_STYLE}>
            <InfoOutlinedIcon sx={WARNING_ICON_STYLE} />
            <Typography variant="body2" sx={WARNING_TEXT_STYLE}>
              {warningText}
            </Typography>
          </Box>
          {onCancel && !isCanceling && !isAisaProcessing && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingTop: 0.5
              }}
            >
              <Button
                aria-label={cancelButtonLabel}
                variant="outlined"
                size="small"
                onClick={onCancel}
              >
                {t("general.cancel")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Fade>
  );
};

export default UploadProgressIndicator;
